import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./index.scss";
import * as _ from "lodash";
import Dropdown from "../../../../../components/core/dropdown";
import { useTranslation } from "react-i18next";
import {
  addProfileRouter,
  editProfileRouter,
  readProfileRouterByProfileId,
} from "../../../../../store/profile-router/thunkAction";
import { listGroup } from "../../../../../store/group/thunkAction";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../utils/event";

const Routing = () => {
  const { t } = useTranslation();
  const params = useParams();
  const profileId = _.get(params, "id");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const profileRouterManage = useSelector((state) => state.profileRouterManage);
  const groupManage = useSelector((state) => state.groupManage);

  const initListGroup = _.get(groupManage, "groups");
  const profileRouter = _.get(profileRouterManage, "profileRouter");
  const profileRouterId = _.get(profileRouter, "_id", null);
  const isLoadingAddProfileRouter = _.get(
    profileRouterManage,
    "isLoadingAddProfileRouter",
    false
  );

  const [title, setTitle] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupDefaultId, setGroupDefaultId] = useState(null);

  const mapDropdownGroup = {
    id: "_id",
    name: "name",
  };

  useEffect(() => {
    dispatch(listGroup());
  }, [dispatch]);

  useEffect(() => {
    dispatch(readProfileRouterByProfileId(profileId));
  }, [dispatch, profileId]);

  useEffect(() => {
    setTitle(_.get(profileRouter, "title", ""));
    setIsActive(_.get(profileRouter, "isActive", false));
    setGroups(_.get(profileRouter, "groups", []));
    setGroupDefaultId(_.get(profileRouter, "groupDefaultId", null));
  }, [profileRouter]);

  const handleSelectGroupDefault = (option) => {
    setGroupDefaultId(option?._id || null);
  };

  const handleGroupsChange = (index, field, value) => {
    const updatedOption = {
      ...groups[index],
      [field]: value,
    };

    const newOptions = groups.map((opt, i) =>
      i === index ? updatedOption : opt
    );

    setGroups(newOptions);
  };

  const addGroup = () => {
    setGroups([...groups, { key: "", value: "" }]);
  };

  const removeGroup = (index) => {
    if (groups.length > 0) {
      const newGroups = groups.filter((_, i) => i !== index);
      setGroups(newGroups);
    }
  };

  const validateRouting = () => {
    if (isActive) {
      const isValid = _.every(
        groups,
        (group) => _.trim(group.key) && _.trim(group.value)
      );
      if (!isValid) {
        sendNotifyIziMessageError(t("profile.invalid_groups"));
        return false;
      }
    }
    return true;
  };

  const saveRouting = async () => {
    const isOk = validateRouting();
    if (!isOk) {
      return false;
    }
    const data = {
      title,
      groupDefaultId: isActive ? null : groupDefaultId,
      groups: isActive ? groups : [],
      isActive,
    };

    try {
      if (profileRouterId) {
        await dispatch(
          editProfileRouter({ id: profileRouterId, data })
        ).unwrap();
        sendNotifyIziMessageSuccess(t("profile.profile_routing_save_success"));
        return;
      }

      if (isLoadingAddProfileRouter) {
        return;
      }

      await dispatch(
        addProfileRouter({
          ...data,
          profileId,
        })
      ).unwrap();
      sendNotifyIziMessageSuccess(t("profile.profile_routing_save_success"));
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_routing_save_error"));
    }
  };

  return (
    <div className="container">
      <div className="card bg-white border-0">
        <div className="card-body" style={{ paddingBottom: "100px" }}>
          <div className="row header-title mb-4">
            <div className="col-lg-6">
              <h3 className="color-pri">
                {t("settings.profile.profile_routing.title_routing")}
              </h3>
            </div>
            <div className="col-lg-6 text-start text-lg-end align-self-center">
              <a
                className="default-link"
                href={`#/profile/${profileId}/detail`}
              >
                <i className="fa fa-arrow-left"></i>{" "}
                {t("settings.profile.profile_detail")}
              </a>
            </div>
            <div className="col-12 color-gray small">
              {t("settings.profile.profile_routing.desc")}
            </div>
          </div>

          <div className="row mb-3 g-3">
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_routing.status")}
              </label>
              <label className="form-check form-switch gap-2 d-flex align-items-center">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span>
                  {isActive ? t("common.enabled") : t("common.disabled")}
                </span>
              </label>
            </div>
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_routing.title")}
              </label>
              <input
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            {!isActive ? (
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.profile_routing.default_group")}
                </label>
                <Dropdown
                  config={{
                    options: initListGroup,
                    map: mapDropdownGroup,
                    onSelect: handleSelectGroupDefault,
                    placeholder: t("settings.profile.profile_routing.everyone"),
                    style: {
                      width: "100%",
                    },
                    value: groupDefaultId,
                    stylePopup: {
                      height: "100px",
                    },
                    className: "button-status",
                  }}
                />
              </div>
            ) : (
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.profile_routing.group")}
                </label>
                {groups.map((group, index) => (
                  <div key={index} className="row align-items-center mb-2">
                    <div className="col-5">
                      <input
                        className="form-control"
                        placeholder="Group name"
                        value={group.key}
                        onChange={(e) =>
                          handleGroupsChange(index, "key", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-5">
                      <Dropdown
                        config={{
                          options: [
                            {
                              _id: "all",
                              name: "settings.profile.profile_routing.all",
                            },
                            ...initListGroup,
                          ],
                          map: mapDropdownGroup,
                          onSelect: (option) => {
                            handleGroupsChange(
                              index,
                              "value",
                              option?._id || null
                            );
                          },
                          placeholder: t(
                            "settings.profile.profile_routing.group"
                          ),
                          style: {
                            width: "100%",
                          },
                          value: group.value,
                          stylePopup: {
                            height: "100px",
                          },
                          className: "button-status",
                        }}
                      />
                    </div>
                    <div className="col-2 text-end">
                      <button
                        className="btn btn-link text-danger"
                        onClick={() => removeGroup(index)}
                      >
                        <i className="fa fa-times-circle"></i>
                      </button>
                    </div>
                  </div>
                ))}

                <div className="row align-items-center">
                  <div className="col-5">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={addGroup}
                    >
                      {t("settings.profile.profile_routing.add_group")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex gap-3 justify-content-center align-items-center">
              <button onClick={saveRouting} className="btn btn-primary">
                {t("common.save")}
              </button>
              <button
                onClick={() => {
                  navigate(`/profile/${profileId}/detail`);
                }}
                className="btn btn-gray btn-outline-gray"
              >
                {t("common.exit")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Routing;
